import { Link } from 'react-router-dom';
import { Menu, X, Syringe, ChevronDown, LogOut, Settings, User } from 'lucide-react';
import { useNavbarState } from '../hooks/useNavbarState';
import { useJournalSettings } from '../hooks/useJournalSettings';

/**
 * The admin-nav branch, extracted verbatim from the pre-theming Navbar.tsx -- unchanged
 * pixel-for-pixel, rendered identically regardless of VITE_THEME. Admin dashboards are
 * explicitly out of scope for theming (see DESIGN_SYSTEM.md: "hasn't been rebranded yet").
 * Calls useNavbarState() itself, matching every theme's own Navbar -- only ever one of
 * AdminNavbarChrome/theme-Navbar is mounted at a time, so this is a single state tree.
 */
export function AdminNavbarChrome({ topBarVisible }: { topBarVisible: boolean }) {
  const settings = useJournalSettings();
  const {
    isOpen,
    setIsOpen,
    scrolled,
    adminUsername,
    activeDropdown,
    mobileDropdownOpen,
    navItems,
    handleDropdownEnter,
    handleDropdownLeave,
    toggleMobileDropdown,
    isActive,
    isChildActive,
    isDropdownItemActive,
    handleLogout,
  } = useNavbarState();

  return (
    <nav
      className={`fixed w-full ${topBarVisible ? 'top-10 sm:top-10' : 'top-0'} transition-all duration-500 ${scrolled
        ? 'bg-white/98 backdrop-blur-md shadow-[0_2px_20px_-2px_rgba(0,0,0,0.1)] border-b border-gray-100'
        : 'bg-white border-b border-gray-100/80'
        } z-40`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 lg:h-[72px]">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500/20 rounded-xl blur-md group-hover:blur-lg transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-green-600 to-emerald-600 p-2 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300">
                  <Syringe className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex flex-col">
                {settings.settingsLoading ? (
                  <span className="inline-block h-6 w-24 bg-gray-200 rounded animate-pulse" aria-hidden="true" />
                ) : (
                  <>
                    <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent tracking-tight">
                      {settings.journalAbbreviation || settings.journalName}
                    </span>
                    <span className="text-[10px] text-gray-500 font-medium tracking-wide hidden lg:block">{settings.journalName}</span>
                  </>
                )}
              </div>
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-0.5">
            {navItems.map((item) => (
              item.children ? (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter(item.name)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${isActive(item.path) || isChildActive(item.children) || activeDropdown === item.name
                      ? 'text-green-700 bg-green-50/80'
                      : 'text-gray-600 hover:text-green-700 hover:bg-gray-50'
                      }`}
                  >
                    {item.name}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                  </button>

                  {activeDropdown === item.name && (
                    <div
                      className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-fadeIn"
                      onMouseEnter={() => handleDropdownEnter(item.name)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      {item.children.map((child, idx) => (
                        <Link
                          key={child.name}
                          to={child.path}
                          state={child.state}
                          className={`flex items-center gap-3 px-4 py-3 text-sm transition-all duration-150 ${isDropdownItemActive(child.path)
                            ? 'text-green-700 bg-green-50'
                            : 'text-gray-600 hover:text-green-700 hover:bg-gray-50'
                            } ${idx === 0 ? '' : 'border-t border-gray-50'}`}
                        >
                          {child.icon && (
                            <div className={`p-1.5 rounded-lg ${isDropdownItemActive(child.path) ? 'bg-green-100' : 'bg-gray-100'}`}>
                              <child.icon className={`h-4 w-4 ${isDropdownItemActive(child.path) ? 'text-green-600' : 'text-gray-500'}`} />
                            </div>
                          )}
                          <span className="font-medium">{child.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : item.isCTA ? (
                <Link
                  key={item.name}
                  to={item.path}
                  className="ml-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md shadow-green-500/25 hover:shadow-lg hover:shadow-green-500/30 hover:from-green-500 hover:to-emerald-500 hover:-translate-y-0.5 active:translate-y-0"
                >
                  {item.icon && <item.icon className="h-4 w-4" />}
                  {item.name}
                </Link>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative group flex items-center ${isActive(item.path)
                    ? 'text-green-700 bg-green-50/80'
                    : 'text-gray-600 hover:text-green-700 hover:bg-gray-50'
                    }`}
                >
                  {item.icon && <item.icon className="h-4 w-4 mr-1.5" />}
                  {item.name}
                  <span
                    className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-300 ${isActive(item.path) ? 'w-3/4 opacity-100' : 'w-0 opacity-0 group-hover:w-1/2 group-hover:opacity-100'
                      }`}
                  ></span>
                </Link>
              )
            ))}

            <div className="h-6 mx-2 w-px bg-gray-200"></div>

            <Link
              to="/admin/settings"
              className="px-4 py-2.5 rounded-lg text-sm font-medium flex items-center text-gray-600 hover:text-green-700 hover:bg-gray-50 transition-all duration-200"
            >
              <Settings className="h-4 w-4 mr-1.5" />
              Settings
            </Link>

            <button
              onClick={handleLogout}
              className="px-4 py-2.5 rounded-lg text-sm font-medium flex items-center text-gray-600 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Logout
            </button>

            <div className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-100 flex items-center">
              <User className="h-4 w-4 mr-1.5" />
              {adminUsername}
            </div>
          </div>

          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-green-600 hover:bg-gray-50 focus:outline-none transition-all duration-200"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden">
          <div className="px-3 pt-3 pb-4 space-y-1 bg-white border-t border-gray-100 shadow-lg animate-fadeIn max-h-[calc(100vh-120px)] overflow-y-auto">
            {navItems.map((item) => (
              item.children ? (
                <div key={item.name} className="space-y-1">
                  <button
                    onClick={() => toggleMobileDropdown(item.name)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${isActive(item.path) || isChildActive(item.children)
                      ? 'text-green-700 bg-green-50'
                      : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                      }`}
                  >
                    <span>{item.name}</span>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${mobileDropdownOpen === item.name ? 'rotate-180' : ''}`} />
                  </button>

                  {mobileDropdownOpen === item.name && (
                    <div className="ml-4 pl-4 border-l-2 border-green-100 space-y-1 animate-fadeIn">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.path}
                          state={child.state}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isDropdownItemActive(child.path)
                            ? 'text-green-700 bg-green-50'
                            : 'text-gray-600 hover:text-green-600 hover:bg-gray-50'
                            }`}
                          onClick={() => {
                            setIsOpen(false);
                            toggleMobileDropdown(item.name);
                          }}
                        >
                          {child.icon && (
                            <div className={`p-1.5 rounded-md ${isDropdownItemActive(child.path) ? 'bg-green-100' : 'bg-gray-100'}`}>
                              <child.icon className={`h-4 w-4 ${isDropdownItemActive(child.path) ? 'text-green-600' : 'text-gray-500'}`} />
                            </div>
                          )}
                          <span>{child.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : item.isCTA ? (
                <Link
                  key={item.name}
                  to={item.path}
                  className="flex items-center justify-center px-4 py-3.5 mt-2 rounded-lg text-base font-semibold transition-all duration-300 bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md shadow-green-500/25 hover:shadow-lg hover:from-green-500 hover:to-emerald-500"
                  onClick={() => setIsOpen(false)}
                >
                  {item.icon && <item.icon className="h-5 w-5 mr-2" />}
                  {item.name}
                </Link>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${isActive(item.path)
                    ? 'text-green-700 bg-green-50'
                    : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                    }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.icon && <item.icon className="h-5 w-5 mr-3" />}
                  {item.name}
                </Link>
              )
            ))}

            <div className="border-t border-gray-100 my-3"></div>

            <Link
              to="/admin/settings"
              className="flex items-center px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-green-600 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <Settings className="h-5 w-5 mr-3" />
              Settings
            </Link>

            <button
              onClick={() => {
                setIsOpen(false);
                handleLogout();
              }}
              className="w-full flex items-center px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Logout
            </button>

            <div className="px-4 py-3 rounded-lg text-base font-medium bg-green-50 text-green-700 flex items-center">
              <User className="h-5 w-5 mr-3" />
              {adminUsername}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default AdminNavbarChrome;
